// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import clsx from 'clsx';

import styles from './CLDLoopLabel.module.css';

/**
 * Loop polarity surfaced to the CLD render path.  Mirrors the
 * `'positive'|'negative'|'unknown'` shape used by CLDArrow so the
 * two components share a vocabulary; the engine's `LoopPolarity` enum
 * is translated to one of these strings at the Editor boundary.
 */
export type CLDLoopPolarity = 'reinforcing' | 'balancing' | 'unknown';

export interface CLDLoopLabelProps {
  /** Loop identifier from the engine; surfaces in the React key. */
  id: string;
  /** Polarity of the feedback loop. */
  polarity: CLDLoopPolarity;
  /** Display label (e.g. `"R1"`, `"B2"`, `"?"`). */
  label: string;
  /**
   * Per-loop badge fill (CO-5, Williams 2026-08-07). When provided it overrides
   * the polarity CSS class so the badge matches the Loop Picker's per-loop color
   * (cyan top-R / slate top-B / distinct hues for the rest). Absent -> fall back
   * to the polarity-based CSS fill.
   */
  fill?: string;
  /** Centroid x coordinate (canvas units, pre-zoom). */
  cx: number;
  /** Centroid y coordinate (canvas units, pre-zoom). */
  cy: number;
  /** When true, the badge is emphasised (its loop is the highlighted one). */
  active?: boolean;
  /** Hover enter/leave; providing it makes the badge interactive (AC2.2). */
  onHoverChange?: (id: string, hovering: boolean) => void;
  /** Click; providing it makes the badge interactive (AC2.2). */
  onSelect?: (id: string) => void;
}

/**
 * D2 acceptance criterion 17 -- the R/B loop label that floats at the
 * centroid of a detected feedback loop.
 *
 * Renders a small filled circle (so the label stays legible
 * regardless of what sits underneath -- connectors, sparklines,
 * background grid) with the polarity glyph centred inside.  Color and
 * the glyph itself both encode polarity so the badge reads in
 * monochrome printing.
 *
 * By default the badge is pointer-events-none and purely decorative -- clicks
 * pass through to whatever sits underneath.  When `onHoverChange`/`onSelect` are
 * supplied (MVP WS2, AC2.2) it becomes interactive: hovering or clicking the
 * badge highlights the loop's member variables on the canvas, and `active`
 * emphasises the badge whose loop is currently highlighted.
 */
export class CLDLoopLabel extends React.PureComponent<CLDLoopLabelProps> {
  render(): React.ReactNode {
    const { id, polarity, label, fill, cx, cy, active, onHoverChange, onSelect } = this.props;
    const interactive = onHoverChange !== undefined || onSelect !== undefined;
    // Per-loop fill (CO-5) wins; otherwise fall back to the polarity CSS class.
    // The label ink stays a single contrasting color set by paper in CSS.
    const polarityFill =
      fill !== undefined
        ? undefined
        : polarity === 'reinforcing'
          ? styles.badgeFillReinforcing
          : polarity === 'balancing'
            ? styles.badgeFillBalancing
            : styles.badgeFillUnknown;
    // Radius is generous enough to host a two-character label
    // ("R12" pushes the limit; truncation/wrap is a future polish
    // task if loop counts grow that high).
    const r = 13;
    return (
      <g
        key={id}
        className={clsx(
          styles.badge,
          interactive && styles.badgeInteractive,
          active && styles.badgeActive,
          'simlin-cld-loop-label',
          `simlin-cld-loop-${polarity}`,
        )}
        onMouseEnter={onHoverChange ? () => onHoverChange(id, true) : undefined}
        onMouseLeave={onHoverChange ? () => onHoverChange(id, false) : undefined}
        onClick={onSelect ? () => onSelect(id) : undefined}
      >
        <circle
          cx={cx}
          cy={cy}
          r={r}
          className={clsx(styles.badgeFill, polarityFill)}
          style={fill !== undefined ? { fill } : undefined}
        />
        <text x={cx} y={cy} className={styles.badgeText}>
          {label}
        </text>
      </g>
    );
  }
}
